import { useEffect, useMemo, useRef, useState } from "react";
import {
  DurableObjectSource,
  type WebSocketLike,
} from "@radar-sim/engine";
import { getAreaData } from "@radar-sim/area-data";
import type {
  AssessmentInstance,
  AssessmentSessionState,
} from "@radar-sim/protocol";
import type { Scenario, WorldSnapshot } from "@radar-sim/schema";
import { drawPPI } from "../ppi/render/draw.js";
import { fnv32 } from "../ppi/render/draw.js";
import { DEFAULT_DISPLAY_STATE } from "../ppi/displayState.js";
import {
  DEFAULT_API,
  buildSessionWebSocketUrl,
  fetchAssessmentState,
  injectAssessmentCommand,
} from "./api.js";
import { ClassroomView } from "./ClassroomView.js";

export interface AssessmentDashboardProps {
  instructorEmail: string;
  assessmentId: string;
  scenarios: ReadonlyArray<Scenario>;
  onLeave: () => void;
}

/**
 * Multi-tile assessment dashboard. Polls the parent assessment DO for the
 * current set of student instances and mounts a thumbnail PPI for each.
 * Click a tile to "take over" — the dashboard switches to the regular
 * `ClassroomView` for that instance, where the instructor can issue
 * commands as if running it solo.
 */
export function AssessmentDashboard(
  props: AssessmentDashboardProps,
): JSX.Element {
  const [state, setState] = useState<AssessmentSessionState | null>(null);
  const [takeoverInstance, setTakeoverInstance] = useState<
    AssessmentInstance | null
  >(null);
  const [error, setError] = useState<string | null>(null);

  // Poll state every 5s.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const next = await fetchAssessmentState(DEFAULT_API, props.assessmentId);
        if (!cancelled) setState(next);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      }
    };
    void tick();
    const handle = setInterval(tick, 5000);
    return () => {
      cancelled = true;
      clearInterval(handle);
    };
  }, [props.assessmentId]);

  if (takeoverInstance !== null && state !== null) {
    return (
      <ClassroomView
        email={props.instructorEmail}
        sessionId={takeoverInstance.instanceSessionId}
        scenarioId={state.scenarioId}
        role="instructor"
        scenarios={props.scenarios}
        onLeave={() => setTakeoverInstance(null)}
      />
    );
  }

  return (
    <div
      style={{
        width: "100%",
        maxWidth: 1280,
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      <DashboardHeader
        state={state}
        onLeave={props.onLeave}
        error={error}
      />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
          gap: 12,
        }}
      >
        {state?.instances.map((inst) => (
          <Tile
            key={inst.instanceSessionId}
            instance={inst}
            assessmentId={props.assessmentId}
            scenarios={props.scenarios}
            scenarioId={state.scenarioId}
            onTakeOver={() => setTakeoverInstance(inst)}
          />
        ))}
        {state !== null && state.instances.length === 0 && (
          <div
            style={{
              gridColumn: "1 / -1",
              padding: 28,
              textAlign: "center",
              color: "var(--text-muted)",
              fontSize: 14,
              background: "var(--bg-card)",
              border: "1px dashed var(--border-strong)",
              borderRadius: "var(--radius-md)",
            }}
          >
            Waiting for students to join with code{" "}
            <strong style={{ fontSize: 18, letterSpacing: 2 }}>
              {state.joinCode}
            </strong>
          </div>
        )}
      </div>
    </div>
  );
}

function DashboardHeader(props: {
  state: AssessmentSessionState | null;
  onLeave: () => void;
  error: string | null;
}): JSX.Element {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        fontSize: 13,
        flexWrap: "wrap",
      }}
    >
      <span
        style={{
          display: "inline-block",
          padding: "2px 10px",
          borderRadius: 999,
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: 0.5,
          textTransform: "uppercase",
          background: "rgba(56,189,248,0.15)",
          color: "var(--accent)",
        }}
      >
        Assessment
      </span>
      {props.state !== null ? (
        <>
          <span style={{ color: "var(--text-muted)" }}>
            join code{" "}
            <strong
              style={{
                color: "var(--accent)",
                fontFamily: "var(--font-mono)",
                letterSpacing: 3,
              }}
            >
              {props.state.joinCode}
            </strong>
          </span>
          <span style={{ color: "var(--text-muted)" }}>
            scenario{" "}
            <code style={{ fontFamily: "var(--font-mono)", color: "var(--text)" }}>
              {props.state.scenarioId}
            </code>
          </span>
          <span style={{ color: "var(--text-muted)" }}>
            {props.state.instances.length} student(s)
          </span>
        </>
      ) : (
        <span style={{ color: "var(--text-muted)" }}>loading…</span>
      )}
      {props.error !== null && (
        <span style={{ color: "var(--danger)" }}>{props.error}</span>
      )}
      <button
        onClick={props.onLeave}
        style={{
          marginLeft: "auto",
          background: "transparent",
          color: "var(--danger)",
          border: "1px solid var(--danger)",
          borderRadius: "var(--radius-sm)",
          padding: "5px 12px",
          fontFamily: "inherit",
          fontSize: 12,
          fontWeight: 500,
          cursor: "pointer",
        }}
      >
        End assessment
      </button>
    </div>
  );
}

function Tile(props: {
  instance: AssessmentInstance;
  assessmentId: string;
  scenarios: ReadonlyArray<Scenario>;
  scenarioId: string;
  onTakeOver: () => void;
}): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [latest, setLatest] = useState<WorldSnapshot | null>(null);
  const [link, setLink] = useState<string>("idle");

  const scenario = props.scenarios.find(
    (s) => s.metadata.id === props.scenarioId,
  );
  const area =
    scenario !== undefined ? getAreaData(scenario.metadata.area) : undefined;

  const source = useMemo(() => {
    const wsUrl = buildSessionWebSocketUrl(
      DEFAULT_API,
      props.instance.instanceSessionId,
    );
    const factory = (): WebSocketLike => {
      const sock = new WebSocket(wsUrl);
      return sock as unknown as WebSocketLike;
    };
    return new DurableObjectSource({ factory });
  }, [props.instance.instanceSessionId]);

  useEffect(() => {
    const stop1 = source.subscribe((s) => setLatest(s));
    const stop2 = source.onStateChange((s) => setLink(s));
    void source.connect();
    return () => {
      stop1();
      stop2();
      void source.disconnect();
    };
  }, [source]);

  // Render the latest snapshot to the tile's canvas.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas === null || latest === null) return;
    const ctx = canvas.getContext("2d");
    if (ctx === null) return;
    drawPPI({
      ctx,
      width: canvas.width,
      height: canvas.height,
      snapshot: latest,
      display: { ...DEFAULT_DISPLAY_STATE, rangeNm: 6 },
      area,
      marpa: [],
      sweepDeg: ((latest.simTimeSeconds * 60) % 360),
      scenarioSeed: fnv32(props.scenarioId),
    });
  }, [latest, area, props.scenarioId]);

  const dropMOB = async () => {
    await injectAssessmentCommand(
      DEFAULT_API,
      props.assessmentId,
      props.instance.studentEmail,
      {
        kind: "inject_event",
        timed: {
          atSimSeconds: (latest?.simTimeSeconds ?? 0) + 0.001,
          event: {
            kind: "mob_drop",
            relativeToOwn: { asternNm: 0.05, abeamNm: 0.02, abeamSide: "port" },
          },
        },
      },
    );
  };

  const fireRacon = async () => {
    await injectAssessmentCommand(
      DEFAULT_API,
      props.assessmentId,
      props.instance.studentEmail,
      {
        kind: "inject_event",
        timed: {
          atSimSeconds: (latest?.simTimeSeconds ?? 0) + 0.001,
          event: {
            kind: "racon_activates",
            raconId: "nab-tower",
            durationSeconds: 30,
          },
        },
      },
    );
  };

  return (
    <div style={tileStyle}>
      <div style={tileHeader}>
        <strong>{props.instance.studentEmail}</strong>
        <span style={{ opacity: 0.6, fontSize: 10 }}>{link}</span>
      </div>
      <canvas
        ref={canvasRef}
        width={260}
        height={260}
        style={{
          width: "100%",
          aspectRatio: "1 / 1",
          borderRadius: "50%",
          background: "#02100b",
          cursor: "pointer",
        }}
        onClick={props.onTakeOver}
      />
      <div style={{ display: "flex", gap: 6 }}>
        <button onClick={props.onTakeOver} style={tileBtn}>
          Take over
        </button>
        <button onClick={dropMOB} style={tileBtn}>
          MOB
        </button>
        <button onClick={fireRacon} style={tileBtn}>
          RACON
        </button>
      </div>
      {latest !== null && (
        <div style={{ fontSize: 10, opacity: 0.6 }}>
          t={Math.floor(latest.simTimeSeconds)}s · {latest.targets.length}{" "}
          targets
        </div>
      )}
    </div>
  );
}

const tileStyle: React.CSSProperties = {
  background: "var(--bg-card)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius-md)",
  padding: 12,
  color: "var(--text)",
  display: "flex",
  flexDirection: "column",
  gap: 10,
};

const tileHeader: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
  fontSize: 12,
  color: "var(--text-muted)",
};

const tileBtn: React.CSSProperties = {
  flex: 1,
  background: "var(--bg-elev)",
  color: "var(--text)",
  border: "1px solid var(--border-strong)",
  borderRadius: "var(--radius-sm)",
  padding: "6px 8px",
  fontFamily: "inherit",
  fontSize: 12,
  fontWeight: 500,
  cursor: "pointer",
};
